import { resolveAuthUserDocument } from '../services/authUserIdentity';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  serverTimestamp
} from 'firebase/firestore';
import { 
  User 
} from 'firebase/auth';

export interface AppUser {
  uid: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phoneCountryCode?: string | null;
  phone?: string | null;
  dateOfBirth?: Date | null;
  yearOfBirth?: number | null;
  gender?: 'male' | 'female' | '';
  nationality?: string | null;
  representing?: string[];
  sendRunningOffers?: boolean;
  notifyFutureEvents?: boolean;
  languagePreference?: 'en' | 'no';
  createdAt?: any;
  lastLogin?: any;
  isAdmin?: boolean;
  // Person ID (assigned by trigger, links to runner results)
  personId?: number;
  // Identity merging fields (set by admin when profiles are linked)
  mergedFrom?: string[];
  mergedAt?: any;
  alternateEmails?: string[];
  // Welcome tour tracking
  hasCompletedWelcomeTour?: boolean;
  // Automatch completion flag (set by trigger after email-based merge check)
  hasCompletedUsersAutomatch?: boolean;
  // Legacy source doc ID (set when auto-merged from another user doc)
  legacySourceUserId?: string;
}

const COLLECTION = 'users';

/**
 * Creates or updates a user document when a user authenticates.
 * 
 * Simple logic:
 * 1. If user doc already exists at users/{uid} → update lastLogin
 * 2. If user doc exists with matching uid field (merged profile) → update lastLogin
 * 3. Otherwise → create new doc at users/{uid}
 * 
 * NOTE: Profile claiming is done explicitly via "This is me!" button,
 * not automatically on login. All claims go through admin tasks.
 */
export async function createOrUpdateUser(user: User) {
  const db = getFirestore();
  const authUid = user.uid;
  const email = user.email?.trim() || null;
  const now = serverTimestamp();
  
  const authDocRef = doc(db, COLLECTION, authUid);
  const existing = await resolveAuthUserDocument(db, authUid);
  if (existing) {
    await setDoc(existing.ref, { email, displayName: user.displayName || '', lastLogin: now }, { merge: true });
    return;
  }

  // Step 3: Create new user doc for this auth user
  await setDoc(authDocRef, {
    uid: authUid,
    email: email,
    displayName: user.displayName || '',
    createdAt: now,
    lastLogin: now,
    hasCompletedWelcomeTour: false, // New users see the welcome tour
  });
}

/**
 * Gets a user by their auth UID.
 * 
 * With identity merging, the user's profile might be at:
 * 1. users/{uid} (standard case)
 * 2. A different doc that has uid field set to this auth UID (merged case)
 * 
 * With archive pattern, unclaimed profiles are moved to usersArchive,
 * so all docs in users collection are "active".
 */
export async function getUser(uid: string): Promise<AppUser | null> {
  const db = getFirestore();
  
  const snapshot = await resolveAuthUserDocument(db, uid);
  return snapshot ? snapshot.data() as AppUser : null;
}

/**
 * Marks the welcome tour as completed for a user.
 * With archive pattern, all docs in users are "active".
 */
export async function markWelcomeTourCompleted(uid: string): Promise<void> {
  const db = getFirestore();
  
  const snapshot = await resolveAuthUserDocument(db, uid);
  if (snapshot) await setDoc(snapshot.ref, {
    hasCompletedWelcomeTour: true, updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function updateUserLanguagePreference(uid: string, languagePreference: 'en' | 'no'): Promise<void> {
  const db = getFirestore();
  
  const snapshot = await resolveAuthUserDocument(db, uid);
  if (snapshot) {
    await setDoc(snapshot.ref, { languagePreference, updatedAt: serverTimestamp() }, { merge: true });
    return;
  }
  const directRef = doc(db, COLLECTION, uid);

  // No doc found - create one (shouldn't normally happen)
  console.warn('[updateUserLanguagePreference] No user doc found for uid:', uid);
  await setDoc(directRef, {
    uid,
    languagePreference,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}
